import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import {
  toReceiveCompositeId,
  useConfirmReceiveArrivalMutation,
  useGetReceiveConfirmContextQuery,
  type ReceiveConfirmArrivalResultDto,
  type ReceiveConfirmLineDto
} from '@/entities/receive';
import { getApiErrorMessage, useProjectSiteParams } from '@/shared/lib';

import { buildConfirmArrivalBody, buildEmptyLineValues, buildFormDefaults } from '../lib/build-payload';
import { CONFIRM_ARRIVAL_CONFLICT_REASON, CONFIRM_ARRIVAL_UNIT_MODE } from './constants';
import { getConfirmArrivalSchema } from './validation';
import type { ConfirmArrivalFormValues, ConfirmArrivalTarget, ConfirmArrivalUnitMode } from './types';

interface UseConfirmArrivalFormArgs {
  target: ConfirmArrivalTarget;
  onClose: () => void;
  /** Fired after a successful receipt — the dashboard uses it to drop stale branches. */
  onSuccess?: (result: ReceiveConfirmArrivalResultDto) => void;
}

const EMPTY_LINES: ReceiveConfirmLineDto[] = [];

export const useConfirmArrivalForm = ({ target, onClose, onSuccess }: UseConfirmArrivalFormArgs) => {
  const { t, i18n } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  /**
   * Only set once the operator actively picks a location. Leaving it undefined
   * on the first call lets the backend answer with its own default destination
   * instead of us guessing one and paying for a second round trip.
   */
  const [locationOverride, setLocationOverride] = useState<string | undefined>();
  const [unitMode, setUnitMode] = useState<ConfirmArrivalUnitMode>(CONFIRM_ARRIVAL_UNIT_MODE.INVENTORY);
  const [result, setResult] = useState<ReceiveConfirmArrivalResultDto | null>(null);

  const shipmentId = useMemo(() => toReceiveCompositeId(siteId, target.shipmentId), [siteId, target.shipmentId]);

  const {
    data: context,
    isLoading,
    isFetching,
    error: contextError,
    refetch
  } = useGetReceiveConfirmContextQuery(
    { shipmentId, projectId, siteId, targetLocationId: locationOverride, lang: i18n.language },
    { skip: !projectId || !siteId }
  );

  const [confirmArrival, { isLoading: isSubmitting }] = useConfirmReceiveArrivalMutation();

  const schema = useMemo(() => getConfirmArrivalSchema(t, context), [t, context]);

  const form = useForm<ConfirmArrivalFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      targetLocationId: '',
      targetLocationSpaceSeq: null,
      actualDateOfReceipt: '',
      comments: '',
      transportDocNumber: '',
      explanationOfLosses: '',
      printGoodsReceivedNote: false,
      lines: []
    }
  });

  const { reset, setValue, getValues } = form;

  // Seeded once per shipment. A later context response (the operator changed the
  // receive location) must not wipe quantities already typed, so the reset is
  // guarded rather than keyed on `context`.
  const seededShipmentRef = useRef<string | null>(null);
  useEffect(() => {
    if (!context || seededShipmentRef.current === shipmentId) return;
    seededShipmentRef.current = shipmentId;
    reset(buildFormDefaults(context));
  }, [context, shipmentId, reset]);

  // A different location brings a different set of storage areas; a selection
  // that no longer exists there would be rejected by the backend.
  useEffect(() => {
    if (!context) return;
    const selected = getValues('targetLocationSpaceSeq');
    if (selected == null) {
      if (context.storageAreas.length === 1) setValue('targetLocationSpaceSeq', context.storageAreas[0].seqno);
      return;
    }
    if (!context.storageAreas.some((area) => area.seqno === selected)) {
      setValue('targetLocationSpaceSeq', context.storageAreas.length === 1 ? context.storageAreas[0].seqno : null);
    }
  }, [context, getValues, setValue]);

  const handleLocationChange = useCallback(
    (locationId: string) => {
      setValue('targetLocationId', locationId, { shouldDirty: true, shouldValidate: true });
      setLocationOverride(locationId);
    },
    [setValue]
  );

  /** Clears every quantity — the legacy "× reset quantities" control. */
  const handleResetQuantities = useCallback(() => {
    const lines = context?.lines ?? EMPTY_LINES;
    setValue('lines', lines.map(buildEmptyLineValues), { shouldDirty: true, shouldValidate: true });
  }, [context, setValue]);

  const submit = useCallback(
    async (values: ConfirmArrivalFormValues) => {
      if (!context || !projectId || !siteId) return;

      try {
        const confirmed = await confirmArrival({
          shipmentId,
          projectId,
          siteId,
          lang: i18n.language,
          body: buildConfirmArrivalBody(values, context)
        }).unwrap();

        setResult(confirmed);
        onSuccess?.(confirmed);
        toast.success(
          t('receive.confirm.success', 'Receipt recorded at {{location}}', {
            location: confirmed.receivedAtLocation
          })
        );
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          t('receive.confirm.error.submitFailed', 'Failed to record the receipt. Please try again.')
        );
        toast.error(message);

        // The outstanding expectation shrank between opening the modal and
        // submitting — another receipt won the race. There is no idempotency
        // key, so the only safe move is to re-read the context and start over.
        if (message.includes(CONFIRM_ARRIVAL_CONFLICT_REASON.QUANTITY_CHANGED)) {
          seededShipmentRef.current = null;
          void refetch();
        }
      }
    },
    [context, projectId, siteId, confirmArrival, shipmentId, i18n.language, onSuccess, t, refetch]
  );

  // Deliberately no `watch()` here: this hook backs the whole modal, so a
  // subscription at this level would re-render every field and every table row
  // on each keystroke. The components that need quantities subscribe to their
  // own slice with `useWatch` instead.
  const lines = context?.lines ?? EMPTY_LINES;

  return {
    form,
    context,
    lines,
    unitMode,
    setUnitMode,
    result,
    isLoading,
    /** True while the location switch re-reads the context — inputs stay usable. */
    isRefreshingContext: isFetching && !isLoading,
    isSubmitting,
    contextError,
    handleLocationChange,
    handleResetQuantities,
    handleSubmit: form.handleSubmit(submit),
    handleClose: onClose
  };
};
